// Adapted from https://github.com/lachlanjc/centresustains/blob/main/components/meta.js
import Head from "next/head";
import { useRouter } from "next/router";
import { PropsWithChildren, ReactNode } from "react";

const makeTitle = (title: string, name: string): string =>
  title === name || title.startsWith(name) ? title : `${title} – ${name}`;

interface Props extends PropsWithChildren<any> {
  title?: string;
  name?: string;
  description?: string;
  image?: string;
  url?: string;
  children?: ReactNode;
}

const color = "#182342";

const org = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Watershed",
  url: "https://watershed.com",
  logo: {
    "@type": "ImageObject",
    url: "https://watershed.com/favicon.svg",
  },
  contactPoint: [
    {
      "@type": "ContactPoint",
      email: "team@watershed.com",
      contactType: "customer support",
      url: "https://watershed.com",
    },
  ],
};

function Meta({
  title = "Watershed",
  name = "Watershed",
  description,
  image = "https://watershed.com/og-image.png",
  url = "https://watershed.com",
  children,
}: Props) {
  const { asPath, locale, locales } = useRouter();
  const imageUrl = image?.startsWith("/") ? url + image : image;
  if (!description) {
    description = `Watershed is the all-in-one software platform for running a climate ${
      locale === "en-GB" ? "programme" : "program"
    }. Reduce your carbon emissions in weeks, not years.`;
  }
  return (
    <Head>
      <meta
        key="og_locale"
        property="og:locale"
        content={locale.replace("-", "_")}
      />
      {locales.map((option) => (
        <link
          rel="alternate"
          key={option}
          hrefLang={option}
          href={`https://watershed.com/${option}${asPath}`}
        />
      ))}
      <meta key="og_type" property="og:type" content="website" />
      <meta key="og_site" property="og:site_name" content={name} />
      <meta key="tw_site" name="twitter:site" content="@watershed" />
      <meta key="og_url" property="og:url" content={url + asPath} />
      <link key="canonical" rel="canonical" href={url + asPath} />
      <title key="title">{makeTitle(title, name)}</title>
      <meta key="og_title" property="og:title" content={title} />
      <meta
        key="tw_title"
        name="twitter:title"
        content={makeTitle(title, name)}
      />
      <meta key="desc" name="description" content={description} />
      <meta key="og_desc" property="og:description" content={description} />
      <meta key="tw_desc" name="twitter:description" content={description} />
      {image && (
        <>
          <meta key="og_img_width" property="og:image:width" content="1200" />
          <meta key="og_img_height" property="og:image:height" content="630" />
          <meta key="og_img" property="og:image" content={imageUrl} />
          <meta
            key="tw_card"
            name="twitter:card"
            content="summary_large_image"
          />
          <meta key="tw_img" name="twitter:image" content={imageUrl} />
        </>
      )}
      <meta key="theme_color" name="theme-color" content={color} />
      <meta key="tile_color" name="msapplication-TileColor" content={color} />
      <link rel="icon" href={`${url}/favicon.svg`} type="image/svg+xml" />
      <link
        key="apple_icon"
        rel="apple-touch-icon"
        sizes="180x180"
        href={`${url}/apple-touch-icon.png`}
      />
      <link
        key="favicon_32"
        rel="icon"
        type="image/png"
        sizes="32x32"
        href={`${url}/favicon-32x32.png`}
      />
      <link
        key="favicon_16"
        rel="icon"
        type="image/png"
        sizes="16x16"
        href={`${url}/favicon-16x16.png`}
      />
      <link key="manifest" rel="manifest" href={`${url}/site.webmanifest`} />
      <script
        key="org_json"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(org) }}
      />
      {children}
    </Head>
  );
}

export default Meta;
